import { deleteWebPushSubscription, saveWebPushSubscription } from "./backend_helper";
import {
    clearWebPushSubscriptionState,
    getExistingServiceWorkerRegistration,
    unsubscribeLocalWebPushSubscription,
} from "./pushSubscriptionCleanup";

const VAPID_PUBLIC_KEY_STORAGE_KEY = "web_push_vapid_public_key";

const urlBase64ToUint8Array = (base64String: string) => {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; i += 1) {
        outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
};

const getVapidPublicKey = () => process.env.REACT_APP_VAPID_PUBLIC_KEY ?? "";

const SERVICE_WORKER_READY_TIMEOUT_MS = 3000;

const getReadyServiceWorkerRegistration = async () => {
    const existingRegistration = await getExistingServiceWorkerRegistration();

    if (existingRegistration?.active) {
        return existingRegistration;
    }

    let timeoutId: number | undefined;

    try {
        return await Promise.race<ServiceWorkerRegistration | undefined>([
            navigator.serviceWorker.ready,
            new Promise<undefined>((resolve) => {
                timeoutId = window.setTimeout(resolve, SERVICE_WORKER_READY_TIMEOUT_MS);
            }),
        ]);
    } finally {
        if (timeoutId) {
            window.clearTimeout(timeoutId);
        }
    }
};

type RegisterPushOptions = {
    requestPermission?: boolean;
};

export const registerWebPushSubscription = async (options: RegisterPushOptions = {}) => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
        return "unsupported";
    }

    const vapidPublicKey = getVapidPublicKey();

    if (!vapidPublicKey) {
        console.warn("Missing REACT_APP_VAPID_PUBLIC_KEY for web push.");
        return "missing-vapid";
    }

    let permission = Notification.permission;

    if (permission === "default" && options.requestPermission) {
        permission = await Notification.requestPermission();
    }

    if (permission !== "granted") {
        return permission;
    }

    const registration = await getReadyServiceWorkerRegistration();

    if (!registration) {
        console.warn("Service worker registration is not ready for web push.");
        return "service-worker-unavailable";
    }

    let subscription = await registration.pushManager.getSubscription();

    const savedVapidPublicKey = localStorage.getItem(VAPID_PUBLIC_KEY_STORAGE_KEY);

    /**
     * Nếu sau này đổi VAPID key lần nữa,
     * thiết bị đã có subscription cũ sẽ được đăng ký lại.
     */
    if (subscription && savedVapidPublicKey !== vapidPublicKey) {
        try {
            console.log("VAPID public key changed. Resetting web push subscription...");

            await subscription.unsubscribe();
            subscription = null;
        } catch (error) {
            console.warn("Failed to unsubscribe old web push subscription:", error);
            subscription = null;
        }
    }

    if (!subscription) {
        subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
        });
    }

    if (subscription) {
        await saveWebPushSubscription(subscription.toJSON());

        localStorage.setItem(VAPID_PUBLIC_KEY_STORAGE_KEY, vapidPublicKey);
    }

    return permission;
};
export const unregisterWebPushSubscription = async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        clearWebPushSubscriptionState();
        return;
    }

    const registration = await getExistingServiceWorkerRegistration();

    if (!registration) {
        clearWebPushSubscriptionState();
        return;
    }

    const subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
        clearWebPushSubscriptionState();
        return;
    }

    let backendError: unknown;

    try {
        await deleteWebPushSubscription(subscription.endpoint);
    } catch (error) {
        backendError = error;
        console.warn("Failed to revoke web push subscription on backend:", error);
    } finally {
        try {
            await subscription.unsubscribe();
        } finally {
            clearWebPushSubscriptionState();
        }
    }

    if (backendError) {
        throw backendError;
    }
};

export { unsubscribeLocalWebPushSubscription };
